import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useSessionStore } from '@/stores/sessionStore'
import { useUIStore } from '@/stores/uiStore'
import { useConnectionHealthStore } from '@/stores/connectionHealthStore'
import { redactPins } from '@/lib/pinScrub'

function decodeJWT(token: string): { exp?: number; iat?: number } | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

function willTokenExpireSoon(token: string, minutesThreshold: number = 2): boolean {
  const decoded = decodeJWT(token)
  if (!decoded?.exp) return false
  
  const expirationTime = decoded.exp * 1000
  const now = Date.now()
  const timeUntilExpiry = expirationTime - now
  
  // Use dynamic threshold: 20% of token lifetime or 2 minutes, whichever is smaller
  // This prevents too-early refresh for short-lived tokens
  const tokenLifetime = (decoded.exp - (decoded.iat || decoded.exp - 600)) * 1000
  const dynamicThreshold = Math.min(tokenLifetime * 0.2, minutesThreshold * 60 * 1000)
  
  const willExpire = timeUntilExpiry <= dynamicThreshold && timeUntilExpiry > 0
  
  const minutesLeft = Math.floor(timeUntilExpiry / 1000 / 60)
  const secondsLeft = Math.floor((timeUntilExpiry / 1000) % 60)
  const thresholdMinutes = Math.floor(dynamicThreshold / 1000 / 60)
  console.log(`[Token Check] Time until expiry: ${minutesLeft}m ${secondsLeft}s | Threshold: ${thresholdMinutes}m | Will refresh: ${willExpire}`)
  
  return willExpire
}

function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token)
  if (!decoded?.exp) return false
  
  const expirationTime = decoded.exp * 1000
  return Date.now() >= expirationTime
}

const API_TIMEOUT_MS = Number(import.meta.env.VITE_API_TIMEOUT_MS ?? 120000)

export const apiClient: AxiosInstance = axios.create({
  baseURL: '',
  timeout: API_TIMEOUT_MS,
  maxRedirects: 0,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-Barnet-Role': 'pos',
  },
  // Repeat arrays without brackets: `?brands=42&brands=87`. The BE uses
  // Tornado's `get_arg_list(name)` which reads `request.arguments[name]`
  // — that key is the literal query-string name, so axios's default
  // `brands[]=...` would land under `request.arguments['brands[]']` and
  // be silently ignored.
  paramsSerializer: { indexes: null },
})


let isRefreshing = false
// Each queued request keeps both a resolve (called with the new token on
// refresh success) and a reject (called with the refresh error on failure).
// Older revisions held only the success callback and cleared the array on
// failure, which orphaned the queued promises forever — a stuck
// `useInfiniteProducts` from a logout-time 403 race left the products grid
// spinning across the next PIN login because React Query still considered
// the previous fetch in-flight.
let refreshSubscribers: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

// Flipped to `true` during an intentional logout (Header.tsx). Every
// redirect path in the response interceptor checks this flag and bails
// out — otherwise an in-flight register-scoped query that resolves
// after we POST /clerk/logout sees a 401, the interceptor "helpfully"
// recovers by clearing state + hard-reloading, and the navigation we
// just queued (/login → PIN screen) gets clobbered by /auth. The flag
// is window-scoped and intentionally not exported as a setter — only
// `beginLogout`/`endLogout` mutate it so its lifecycle is auditable.
let isLoggingOut = false

export function beginLogout(): void {
  isLoggingOut = true
}

export function endLogout(): void {
  isLoggingOut = false
}

/**
 * Read-only view of the in-flight token-refresh flag, for DIAGNOSTICS ONLY.
 * The terminal-poll watchdog beacons this to tell a poll stalled behind a
 * stuck token refresh apart from other stall causes. Do NOT gate behavior
 * on it — it's observability, not control.
 */
export function isRefreshingNow(): boolean {
  return isRefreshing
}

function onRefreshed(token: string) {
  // Snapshot + clear before invoking so a synchronous re-entry from a
  // subscriber doesn't double-fire or skip later entries.
  const subs = refreshSubscribers
  refreshSubscribers = []
  subs.forEach(({ resolve }) => resolve(token))
}

function onRefreshFailed(error: unknown) {
  const subs = refreshSubscribers
  refreshSubscribers = []
  subs.forEach(({ reject }) => reject(error))
}

function addRefreshSubscriber(
  resolve: (token: string) => void,
  reject: (error: unknown) => void,
) {
  refreshSubscribers.push({ resolve, reject })
}

// /v2/auth/refresh has surfaced a few different response shapes across BE
// revisions: the canonical {session_token, refresh_token} at top level, an
// envelope-wrapped {success, data: {session_token, ...}}, and at one point
// {access_token, ...}. Naively `const {session_token} = response.data`
// silently destructures `undefined` from the unsupported shapes and then
// writes that into the session store — corrupting state with `sessionToken
// = undefined`, which downstream presents as "user got logged out for no
// reason". Probe the well-known shapes explicitly and fail loud if none
// match.
function extractRefreshTokens(
  payload: unknown,
): { session_token: string; refresh_token: string | null; clerk_token: string | null } | null {
  if (!payload || typeof payload !== 'object') return null
  const root = payload as Record<string, unknown>
  const candidates: Array<Record<string, unknown>> = [root]
  if (root.data && typeof root.data === 'object') {
    candidates.push(root.data as Record<string, unknown>)
  }
  for (const c of candidates) {
    const session = c.session_token ?? c.access_token
    if (typeof session === 'string' && session.length > 0) {
      const refresh = c.refresh_token
      // BE re-mints clerk_token bound to the new session JTI when we
      // send the old clerk_token in the refresh body (pos/auth.py
      // L352-396). Read it back so the store stays in sync; absent
      // when caller didn't send a clerk_token, when clerk decode
      // failed BE-side, or when the old clerk_token expired past the
      // max_age_seconds window.
      const clerk = c.clerk_token
      return {
        session_token: session,
        refresh_token: typeof refresh === 'string' ? refresh : null,
        clerk_token: typeof clerk === 'string' ? clerk : null,
      }
    }
  }
  return null
}

async function refreshTokenIfNeeded(): Promise<string | null> {
  const { sessionToken, refreshToken, clerkToken, backendUrl } = useSessionStore.getState()

  if (!sessionToken) return null

  // Session-token refresh. The clerk_token is bound to the current
  // session JTI (jwt_auth.py L273); refreshing the session mints a
  // new JTI, which would orphan the clerk and 403 every register-
  // scoped request until re-PIN. Sending the old clerk_token along
  // tells the BE to re-mint a fresh clerk bound to the new session
  // JTI (pos/auth.py L352-396), keeping the cashier logged in across
  // session refreshes.
  const sessionNeedsRefresh = willTokenExpireSoon(sessionToken, 2) || isTokenExpired(sessionToken)
  if (!sessionNeedsRefresh) {
    return sessionToken
  }

  console.log('[Token Refresh] Session token needs refresh, initiating...')

  if (isRefreshing) {
    console.log('[Token Refresh] Waiting for ongoing refresh...')
    return new Promise<string | null>((resolve) => {
      addRefreshSubscriber(
        (newToken: string) => resolve(newToken),
        // Match the failure-path behavior of the standalone branch
        // below: return null so the caller proceeds with whatever
        // token is currently on the request and lets the response
        // interceptor handle any resulting 401/403.
        () => resolve(null),
      )
    })
  }

  isRefreshing = true

  try {
    console.log('[Token Refresh] Calling /v2/auth/refresh...')
    // BE reads `refresh_token` from the body and re-mints the
    // session_token (V2AuthRefreshHandler delegates to v1 PosAuthRefresh,
    // which 401s if the body is missing). Bearer header isn't required
    // — these handlers don't run session auth — but we attach it so
    // request logs still tie a refresh to the previous session.
    //
    // We also include the current clerk_token (when present) so the
    // BE re-issues it bound to the new session JTI. Without this, the
    // post-refresh clerk_token still carries the old session_jti and
    // every register-scoped request 403s until the cashier re-PINs.
    const response = await axios.post(
      `${backendUrl}/ht/api/v2/auth/refresh`,
      {
        refresh_token: refreshToken,
        ...(clerkToken ? { clerk_token: clerkToken } : {}),
      },
      { headers: { Authorization: `Bearer ${sessionToken}` } },
    )

    const newTokens = extractRefreshTokens(response.data)
    if (!newTokens) {
      // Keys only — replay console capture ships these logs to PostHog,
      // and the raw payload carries session/refresh tokens.
      console.error('[Token Refresh] ✗ Refresh response missing session_token; payload keys:', Object.keys(response.data ?? {}))
      isRefreshing = false
      onRefreshFailed(new Error('Refresh response missing session_token'))
      return null
    }

    useSessionStore.getState().setSessionTokens(
      newTokens.session_token,
      newTokens.refresh_token ?? refreshToken ?? '',
      // Forward the rebound clerk_token when BE returned one. Pass
      // `undefined` (not `null`) to leave the store's clerkToken
      // untouched — null would actively clear it, breaking flows
      // where the BE legitimately omitted the field (no clerk sent,
      // or clerk too old to re-bind).
      newTokens.clerk_token ?? undefined,
    )

    if (clerkToken && !newTokens.clerk_token) {
      // We SENT a clerk_token and the BE refreshed the session WITHOUT
      // re-minting it — its explicit signal the clerk grant can't be
      // rebound (decode failed / past the re-mint window). The session
      // is now a NEW jti generation, so the stored clerk is a
      // guaranteed session_jti mismatch: every register-scoped request
      // from here 403s forever (the zombie-register state). Don't let
      // callers proceed into that — fail the waiters and route to PIN
      // re-entry while the fresh session is preserved for it.
      console.warn('[Token Refresh] Session refreshed but BE did not re-mint the clerk token')
      isRefreshing = false
      onRefreshFailed(new Error('clerk token could not be rebound'))
      recoverDeadClerkSession('session refreshed but clerk token could not be rebound')
      return null
    }

    isRefreshing = false
    onRefreshed(newTokens.session_token)

    console.log('[Token Refresh] ✓ Session token refreshed' + (newTokens.clerk_token ? ' (clerk rebound)' : ''))
    return newTokens.session_token
  } catch (error) {
    // Message only — an AxiosError's toJSON embeds config.data, i.e. the
    // refresh_token, and replay console capture ships this to PostHog.
    console.error('[Token Refresh] ✗ Failed to refresh token:', error instanceof Error ? error.message : String(error))
    isRefreshing = false
    onRefreshFailed(error)

    // 401 from refresh = unrecoverable: the refresh token / session is no
    // longer valid. Bail out to login now instead of returning null and
    // letting the original request fly with a stale token (which would
    // 401 again and force a redirect via the response interceptor's
    // _retry path — same end state, extra round trip, no upside).
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const { clerkToken } = useSessionStore.getState()
      if (clerkToken) {
        useSessionStore.getState().clearClerkAuth()
        window.location.href = '/select-register'
      } else {
        useSessionStore.getState().clearSession()
        window.location.href = '/auth'
      }
    }

    return null
  }
}

// Marker on the synthetic rejection minted while the clerk re-PIN overlay
// is up (see the circuit breaker in the request interceptor below).
// Callers don't need to special-case it — it surfaces like any network
// failure — but it's taggable for anyone who wants to hide "reconnecting"
// UX behind the overlay.
export const CLERK_REAUTH_PAUSED_FLAG = '__waioClerkReauthPaused'

export function isClerkReauthPausedError(err: unknown): boolean {
  return !!(
    err
    && typeof err === 'object'
    && (err as Record<string, unknown>)[CLERK_REAUTH_PAUSED_FLAG] === true
  )
}

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { backendUrl, sessionToken } = useSessionStore.getState()

    // Circuit breaker while the clerk re-PIN overlay is up. Every
    // register-scoped call is guaranteed to 403 until a fresh clerk
    // exists, but resilient callers (the cart preview bridge's
    // reconnect loop, query invalidations) keep retrying — and each
    // retry used to drag a full token-refresh round-trip with it:
    // BE/auth churn, console + session-replay spam, refresh-token
    // rotation for nothing. Fail these locally and instantly instead;
    // the moment the PIN succeeds the flag clears and every loop
    // resumes unchanged. The auth family stays exempt — the PIN submit
    // itself is /v2/auth/clerk.
    if (useUIStore.getState().clerkReauthRequired !== null
        && !/\/(pos|v2)\/auth\//.test(config.url || '')) {
      const pausedErr = new Error(
        'request paused: clerk re-authentication in progress',
      ) as Error & Record<string, unknown>
      pausedErr[CLERK_REAUTH_PAUSED_FLAG] = true
      return Promise.reject(pausedErr)
    }

    if (backendUrl) {
      config.baseURL = backendUrl
    }

    // Endpoints that must NOT trigger a proactive refresh:
    //   - /auth/token: initial login, no session yet.
    //   - /auth/refresh: would loop on itself.
    //   - /auth/select-store: rides on the manager user_token, not the
    //     session_token; refreshing here is meaningless.
    // Everything else under /auth/ (clerk, switch-store, stores, …)
    // rides on the session_token and benefits from the same proactive
    // refresh as regular API calls — without this, a kiosk that sat
    // idle past the session token's expiry hit "Invalid or expired
    // session token" on the next PIN entry instead of silently rolling
    // the token over.
    const isRefreshSkipEndpoint = /\/(pos|v2)\/auth\/(token|refresh|select-store)\b/.test(config.url || '')
    // Caller-supplied Authorization headers win — used by the manager-
    // flow leg that exchanges `user_token` for a session_token via
    // /v2/auth/select-store, and by /v2/auth/stores. Without this guard
    // the interceptor would clobber the user_token Bearer with a stale
    // session_token from a previous login.
    const callerAuth = config.headers.get('Authorization')

    if (!isRefreshSkipEndpoint) {
      if (callerAuth) {
        // Explicit override — leave it.
      } else {
        const freshToken = await refreshTokenIfNeeded()
        if (freshToken) {
          config.headers.set('Authorization', `Bearer ${freshToken}`)
        } else if (sessionToken) {
          config.headers.set('Authorization', `Bearer ${sessionToken}`)
        }
      }
    } else if (!callerAuth && sessionToken) {
      // /v2/auth/refresh authenticates via the current session_token.
      config.headers.set('Authorization', `Bearer ${sessionToken}`)
    }

    // Re-read the clerk token from the store AFTER the refresh above — do NOT
    // use a value captured at the top of this interceptor. A proactive
    // refreshTokenIfNeeded() re-mints the clerk bound to the NEW session jti and
    // writes it to the store; a clerk captured before that await is stale.
    // Attaching a stale clerk (session_jti = old jti) alongside the freshly
    // refreshed session is a session_jti mismatch, which the BE rejects with
    // 403 'v2 requires X-POS-Clerk-Token' mid-session until a re-PIN. Reading it
    // here keeps the session + clerk pair consistent.
    const clerkToken = useSessionStore.getState().clerkToken
    if (clerkToken) {
      config.headers.set('X-POS-Clerk-Token', clerkToken)
    }

    // Connection-health timing stamp — LAST, after every await above
    // (notably the proactive refreshTokenIfNeeded), so the duration
    // sample measures the request's own round-trip, not token
    // maintenance that happened to precede it.
    ;(config as InternalAxiosRequestConfig & { __healthStartAt?: number })
      .__healthStartAt = performance.now()

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Skip auth-recovery side effects (clearSession, clearClerkAuth, hard
// navigation) when the failing request originated from the customer-facing
// display. The CFD is a passive viewer — its bootstrap query failing is
// a non-event, but `clearSession()` would wipe the POS-shared persisted
// session and `window.location.href = '/auth'` would bounce through to
// `/connect` (since /auth's beforeLoad checks backendUrl, which the
// clearSession reload zeroed out). Returning true here means the
// interceptor still rejects the promise; the query just stays errored.
function isCfdWindow(): boolean {
  return typeof window !== 'undefined'
    && window.location.pathname.startsWith('/customer')
}

// --- Clerk-session recovery -------------------------------------------------
// The BE validates the clerk JWT per-request against the SESSION token's
// jti (jwt_auth.py check_jwt_auth): a clerk bound to a previous session
// generation 403s every register-scoped call. The 2026-07 Ontario "zombie
// register" incidents were exactly this state — UI alive, all calls 403,
// refresh "succeeding" without helping — held indefinitely because nothing
// routed the cashier back to PIN entry. This helper is that route-out.
//
// Deliberately narrow: callers gate on 403 (the clerk-rejection status;
// 401 post-logout artifacts are the class that got an earlier blanket
// recovery removed — see the _retry branch comment), and this function
// itself refuses when there is no clerk to recover, during logout, and in
// the CFD window. Single-flight so a burst of parallel 403s produces one
// navigation. The reason string is stashed for the select-register page
// to surface as a toast after the reload — the graceful half.
const CLERK_RECOVERY_FLAG = 'waio.clerk_recovery_reason'
let clerkRecoveryInFlight = false

function recoverDeadClerkSession(reason: string): boolean {
  if (clerkRecoveryInFlight) return true
  if (isLoggingOut || isCfdWindow()) return false

  const { clerkToken, isAuthenticated } = useSessionStore.getState()

  // Inside the POS shell, recover IN PLACE: drop only the dead clerk
  // token and raise the non-dismissable PIN overlay
  // (ClerkPinReauthModal, mounted in POSLayout). The screen, cart, and
  // query cache all survive; re-PIN + refetch resumes work in seconds.
  // Also fires when the clerk token is ALREADY absent on an
  // authenticated shell — the state a reload during the overlay
  // rehydrates into (clerkToken null persisted, isAuthenticated true):
  // without this, post-reload 403s would find no clerk to invalidate
  // and fall through to the silent zombie again. The overlay flag is
  // idempotent, and a null clerkToken makes invalidateClerkToken a
  // no-op, so repeat calls are harmless.
  if (typeof window !== 'undefined'
      && window.location.pathname.startsWith('/pos')
      && (clerkToken || isAuthenticated)) {
    console.warn('[Clerk Recovery] %s — raising in-place PIN re-entry overlay', reason)
    useSessionStore.getState().invalidateClerkToken()
    useUIStore.getState().requireClerkReauth(reason)
    return true
  }

  if (!clerkToken) return false

  // Outside the POS shell (no overlay mounted): full clerk teardown and
  // hard navigation, with the reason stashed for select-register's toast.
  clerkRecoveryInFlight = true
  console.warn('[Clerk Recovery] %s — clearing clerk auth, routing to PIN re-entry', reason)
  try {
    sessionStorage.setItem(CLERK_RECOVERY_FLAG, reason)
  } catch {
    // Storage unavailable — the banner is best-effort; recovery proceeds.
  }
  useSessionStore.getState().clearClerkAuth()
  window.location.href = '/select-register'
  // The hard navigation tears down this module (and the latch with it);
  // the timer only matters if the navigation is somehow prevented.
  // Release after a grace window so a genuinely stuck tab regains the
  // ability to recover instead of suppressing every future attempt for
  // its lifetime.
  setTimeout(() => {
    clerkRecoveryInFlight = false
  }, 10000)
  return true
}

// Marker stamped on the synthetic error we raise when a 2xx body is an HTML
// login/proxy interstitial instead of JSON (captive portal, LB sign-in page,
// backend redirect). That error has no `.response.status`, so without a marker
// it's indistinguishable from any other opaque throw. `classifyPreviewError`
// (api/v2.ts) reads this to tag the failure 'transport' (the device reached
// *something* and it'll clear on its own once the portal does) rather than
// mislabel it — so the cart's reconnect UX can say so truthfully and the retry
// loop keeps trying. Property name is namespaced to avoid colliding with
// axios/Error fields.
const HTML_INTERSTITIAL_FLAG = '__waioHtmlInterstitial'

export function isHtmlInterstitialError(err: unknown): boolean {
  return !!(
    err
    && typeof err === 'object'
    && (err as Record<string, unknown>)[HTML_INTERSTITIAL_FLAG] === true
  )
}

apiClient.interceptors.response.use(
  (response) => {
    // Detect HTML responses (backend redirect to login page) as unauthorized
    const contentType = response.headers['content-type'] || ''
    if (contentType.includes('text/html') && !response.config.url?.includes('/auth/')) {
      // Captive-portal sighting: SOMETHING answered, but not our API.
      // Feeds the connection-health reason attribution.
      useConnectionHealthStore.getState().recordCaptivePortal()
      const htmlErr = new Error('Unauthorized') as Error & Record<string, unknown>
      htmlErr[HTML_INTERSTITIAL_FLAG] = true
      return Promise.reject(htmlErr)
    }
    // Connection health (passive): an answered 2xx is reachability proof
    // AND a latency sample -- except retried requests, whose duration
    // spans the auth-refresh round-trip (reachability only, no sample).
    const cfg = response.config as InternalAxiosRequestConfig & {
      __healthStartAt?: number
      _retry?: boolean
    }
    const durationMs =
      typeof cfg.__healthStartAt === 'number'
        ? performance.now() - cfg.__healthStartAt
        : null
    useConnectionHealthStore.getState().recordApiSuccess(cfg._retry ? null : durationMs)
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    if (!originalRequest) return Promise.reject(error)

    // Connection health (passive): an answered error still proves the
    // server is reachable (4xx/5xx are not connectivity failures); only
    // no-response errors count toward offline. Canceled/superseded
    // requests are neither. The circuit-breaker's synthetic rejections
    // and the HTML-interstitial error never reach here -- they carry no
    // config, so the guard above already returned.
    if (!axios.isCancel(error) && (error as { code?: string }).code !== 'ERR_CANCELED') {
      if (error.response) {
        useConnectionHealthStore.getState().recordApiSuccess(null)
      } else {
        useConnectionHealthStore.getState().recordApiNetworkFailure()
      }
    }


    if (originalRequest._retry) {
      // ONE narrow exception to the no-recovery rule below (2026-07
      // Ontario zombie registers): a RETRIED request that still 403s
      // while we hold a clerk token means the freshly-refreshed session
      // + current clerk pair is being rejected by the BE's session_jti
      // binding — a state that never self-heals and bricks every
      // register-scoped call until re-PIN. Exclusions:
      //   - 401s (the post-logout artifact class that justified
      //     removing the old blanket recovery),
      //   - BUSINESS 403s: permission decisions like
      //     cashier_unauthorized have their own flows (manager override
      //     PIN). They arrive as a v2 JSON envelope — an object with
      //     `success: false` — regardless of whether `code` is set
      //     (V2ErrorBody allows code: null). The auth-layer clerk
      //     rejection (tornado die()) renders a non-envelope body, so
      //     envelope shape, not code presence, is the discriminator.
      //     Envelope 403s fall through to the caller untouched.
      //   - CFD window / logout — recoverDeadClerkSession enforces
      //     those and returns false to fall through to the plain
      //     reject.
      const errBody = error.response?.data as
        | { success?: unknown }
        | string
        | null
        | undefined
      const isV2EnvelopeError =
        !!errBody && typeof errBody === 'object'
        && (errBody as { success?: unknown }).success === false
      if (error.response?.status === 403
          && !isV2EnvelopeError
          && recoverDeadClerkSession('register-scoped 403 persisted after token refresh')) {
        return Promise.reject(error)
      }
      console.error('[Token Refresh] Already retried this request, giving up')
      // Deliberately NO state mutation or hard-reload from this branch.
      // Earlier revisions called `clearSession() + window.location='/auth'`
      // (or `clearClerkAuth() + /select-register`) here on retry-still-401.
      // That was wrong: the most common reason the retry 401s is
      // post-logout in-flight register-scoped queries that the BE
      // already invalidated — a transport-layer artifact, not a sign
      // the session is actually dead. The aggressive recovery path
      // repeatedly logged users out (and corrupted state for windows
      // sharing localStorage). Just reject; navigation is the
      // application's responsibility (route guards + explicit logout
      // mutations), not the interceptor's.
      return Promise.reject(error)
    }

    // Same allowlist as the request interceptor — only token/refresh/
    // select-store should bypass the refresh-on-401 retry. /auth/clerk
    // and /auth/switch-store legitimately ride on a session_token that
    // can expire mid-flight.
    const isRefreshSkipEndpoint = /\/(pos|v2)\/auth\/(token|refresh|select-store)\b/.test(originalRequest.url || '')

    // While the clerk re-PIN overlay is up, don't spin refresh cycles for
    // stragglers that were already in flight when the circuit breaker
    // closed — their 401/403 is expected; just reject. The overlay's PIN
    // success is what restores service, not another refresh.
    //
    // The auth family is exempt, mirroring the request-side breaker: the
    // PIN submit itself (/v2/auth/clerk) must keep its refresh+retry
    // safety net — if the session token is server-invalid but not
    // locally expired, the PIN attempt 401s, the refresh below re-mints
    // the session, and the retried PIN lands. Blocking that here left
    // the cashier failing PIN entry forever with no way out but Logout.
    if (useUIStore.getState().clerkReauthRequired !== null
        && !/\/(pos|v2)\/auth\//.test(originalRequest.url || '')) {
      return Promise.reject(error)
    }

    if ((error.response?.status === 401 || error.response?.status === 403) &&
        !isRefreshSkipEndpoint) {
      const { sessionToken, clerkToken } = useSessionStore.getState()

      if (!sessionToken) {
        // No session token to refresh with. Don't auto-logout from
        // here — that races with route guards (which check sessionToken
        // and redirect explicitly) and with the logout flow (which
        // expects to control its own navigation). Just reject.
        console.error('[Token Refresh] No session token available')
        return Promise.reject(error)
      }

      if (!isRefreshing) {
        isRefreshing = true

        try {
          const {
            backendUrl,
            sessionToken: currentSessionToken,
            refreshToken: currentRefreshToken,
            clerkToken: currentClerkToken,
          } = useSessionStore.getState()

          // Mirrors refreshTokenIfNeeded — refresh_token in body is the
          // contract V2AuthRefreshHandler/PosAuthRefreshHandler enforce.
          // Forward the current clerk_token so the BE re-mints it bound
          // to the new session JTI; otherwise the retry below would
          // succeed on the session-auth check but still 403 on
          // register-scoped endpoints because the clerk_token's
          // session_jti claim references the now-dead JTI.
          const response = await axios.post(
            `${backendUrl}/ht/api/v2/auth/refresh`,
            {
              refresh_token: currentRefreshToken,
              ...(currentClerkToken ? { clerk_token: currentClerkToken } : {}),
            },
            { headers: { Authorization: `Bearer ${currentSessionToken}` } },
          )

          const newTokens = extractRefreshTokens(response.data)
          if (!newTokens) {
            // Loud fail rather than corrupting the session with
            // `sessionToken = undefined`. Surface this as a refresh
            // failure so the catch below decides what to do. Keys only:
            // the raw payload carries tokens and replay console capture
            // ships these logs to PostHog.
            console.error(
              '[Token Refresh] ✗ Refresh succeeded HTTP-wise but payload is missing session_token; payload keys:',
              Object.keys(response.data ?? {}),
            )
            isRefreshing = false
            onRefreshFailed(error)
            return Promise.reject(error)
          }

          useSessionStore.getState().setSessionTokens(
            newTokens.session_token,
            newTokens.refresh_token ?? currentRefreshToken ?? '',
            // See refreshTokenIfNeeded — undefined leaves clerkToken
            // untouched; only update when BE returned a rebound one.
            newTokens.clerk_token ?? undefined,
          )

          if (currentClerkToken && !newTokens.clerk_token) {
            // Clerk sent, none returned: the BE refused to rebind it to
            // the new session generation. Retrying is pointless — the
            // stored clerk is now a guaranteed session_jti mismatch —
            // so fail the queued waiters and route to PIN re-entry
            // (see refreshTokenIfNeeded's twin branch).
            console.warn('[Token Refresh] Session refreshed but BE did not re-mint the clerk token')
            isRefreshing = false
            onRefreshFailed(error)
            recoverDeadClerkSession('session refreshed but clerk token could not be rebound')
            return Promise.reject(error)
          }

          isRefreshing = false
          onRefreshed(newTokens.session_token)

          console.log('[Token Refresh] ✓ Session token refreshed, retrying request')

          // Mark as retried BEFORE retrying to prevent infinite loops
          originalRequest._retry = true

          // Retry original request with new token — session AND clerk.
          // The refresh above re-mints the clerk bound to the NEW session
          // jti and stores it; replaying this request's ORIGINAL (stale)
          // X-POS-Clerk-Token header alongside the new session token is a
          // guaranteed session_jti mismatch -> 403 -> give-up, which is
          // precisely the zombie-register loop. The queued-subscriber
          // path below already did this; the primary path missed it.
          originalRequest.headers.set('Authorization', `Bearer ${newTokens.session_token}`)
          const reboundClerkToken = useSessionStore.getState().clerkToken
          if (reboundClerkToken) {
            originalRequest.headers.set('X-POS-Clerk-Token', reboundClerkToken)
          } else {
            originalRequest.headers.delete('X-POS-Clerk-Token')
          }
          return apiClient(originalRequest)
        } catch (refreshError) {
          // Message only — see the refresh catch above re: AxiosError toJSON.
          console.error('[Token Refresh] ✗ Failed to refresh session token:', refreshError instanceof Error ? refreshError.message : String(refreshError))
          isRefreshing = false
          onRefreshFailed(error)

          // Be conservative about side effects on refresh failure.
          // Two flavors of failure:
          //   1. Network/5xx/timeout — almost always transient. Don't
          //      clear state; let the failing query surface an error
          //      and the user (or a retry) recover.
          //   2. BE 401/403 on /v2/auth/refresh — looks like a real
          //      session expiry. Even then we DON'T blanket-clearSession
          //      when `clerkToken` is null: that combination is the
          //      classic post-clerk-logout state, and some BEs
          //      invalidate the session_token alongside the clerk
          //      session on /clerk/logout. Auto-blowing away the
          //      session on top of that puts a manager who could
          //      otherwise just /select-store back at /auth re-entering
          //      credentials. If the session truly is dead, the next
          //      authenticated action will reveal it.
          const refreshStatus =
            (refreshError as { response?: { status?: number } } | null)?.response?.status
          const sessionGenuinelyExpired = refreshStatus === 401 || refreshStatus === 403

          if (sessionGenuinelyExpired && !isLoggingOut && !isCfdWindow() && clerkToken) {
            useSessionStore.getState().clearClerkAuth()
            window.location.href = '/select-register'
          }

          return Promise.reject(error)
        }
      }

      // Another request hit 401/403 while refresh is in-flight — queue it
      return new Promise((resolve, reject) => {
        addRefreshSubscriber(
          (newToken: string) => {
            // Mark as retried to prevent infinite loops
            originalRequest._retry = true

            // Update both session and clerk tokens
            originalRequest.headers.set('Authorization', `Bearer ${newToken}`)
            const { clerkToken: updatedClerkToken } = useSessionStore.getState()
            if (updatedClerkToken) {
              originalRequest.headers.set('X-POS-Clerk-Token', updatedClerkToken)
            }

            apiClient(originalRequest).then(resolve).catch(reject)
          },
          // Refresh failed — surface the original 401/403 to React
          // Query so the observer transitions to error state. Without
          // this, the promise hung forever and any future invalidate
          // saw the query as still in-flight, never refetching.
          () => reject(error),
        )
      })
    }

    return Promise.reject(error)
  }
)

export interface ApiResponse<T> {
  data: T
  status: 'ok' | 'error'
  error?: string
  message?: string
}

export interface PaginatedParams {
  page?: number
  per_page?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

// API request/response logging is dev-only. In production these calls
// would dump customer PII, payment metadata, and last4 to the console
// — devtools left open at the till is a real exposure path. The
// browser's network tab is still available for debugging in prod.
const DEV = import.meta.env.DEV
// Even in DEV, never print a PIN (a manager's override_pin, check-manager-
// access): dev consoles get screen-shared and pasted into tickets.

export const api = {
  get: async <T>(url: string, params?: Record<string, unknown>, opts?: { signal?: AbortSignal }) => {
    if (DEV) console.log('[API] GET request:', { url, params: redactPins(params), timestamp: new Date().toISOString() })
    // Forward AbortSignal so callers (e.g. useSearchOrders) can cancel a
    // superseded request when the user keeps typing — otherwise old
    // searches keep running on the BE and pile up DB seq-scans.
    const response = await apiClient.get<T>(url, { params, signal: opts?.signal })
    if (DEV) console.log('[API] GET response:', { url, status: response.status, timestamp: new Date().toISOString() })
    return response.data
  },

  post: async <T>(url: string, data?: object, opts?: { signal?: AbortSignal }) => {
    if (DEV) console.log('[API] POST request:', { url, data: redactPins(data), timestamp: new Date().toISOString() })
    // Forward AbortSignal so callers (e.g. useCartPreview) can actually
    // cancel a slow in-flight request when the cart shape moves on.
    // Without this, the second preview goes out before the first returns
    // and the BE pays for both.
    const response = await apiClient.post<T>(url, data, opts?.signal ? { signal: opts.signal } : undefined)
    if (DEV) console.log('[API] POST response:', { url, status: response.status, data: redactPins(response.data), timestamp: new Date().toISOString() })
    return response.data
  },

  put: async <T>(url: string, data?: object) => {
    if (DEV) console.log('[API] PUT request:', { url, data: redactPins(data), timestamp: new Date().toISOString() })
    const response = await apiClient.put<T>(url, data)
    if (DEV) console.log('[API] PUT response:', { url, status: response.status, timestamp: new Date().toISOString() })
    return response.data
  },

  patch: async <T>(url: string, data?: object) => {
    if (DEV) console.log('[API] PATCH request:', { url, data: redactPins(data), timestamp: new Date().toISOString() })
    const response = await apiClient.patch<T>(url, data)
    if (DEV) console.log('[API] PATCH response:', { url, status: response.status, timestamp: new Date().toISOString() })
    return response.data
  },

  delete: async <T>(url: string, data?: object) => {
    if (DEV) console.log('[API] DELETE request:', { url, data: redactPins(data), timestamp: new Date().toISOString() })
    const response = await apiClient.delete<T>(url, { data })
    if (DEV) console.log('[API] DELETE response:', { url, status: response.status, timestamp: new Date().toISOString() })
    return response.data
  },
}

export default apiClient
